<script setup lang="ts">
import { router } from '@inertiajs/vue3';
import { CheckCircle, Link2, Link2Off } from 'lucide-vue-next';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@/components/ui/card';
import * as zoteroRoutes from '@/routes/zotero';

const { zotero } = defineProps<{
    zotero: {
        connected: boolean;
        zotero_user_id?: string;
        connected_at?: string;
        last_synced_at?: string;
    };
}>();

function formatDate(d?: string) {
    if (!d) {
        return '—';
    }

    return new Date(d).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    });
}

async function connect() {
    const res = await fetch('/integrations/zotero/auth/url');
    const data = await res.json();

    if (data.url) {
        window.location.href = data.url;
    }
}

function disconnect() {
    router.post(zoteroRoutes.disconnect().url);
}
</script>

<template>
    <div class="max-w-2xl space-y-6 p-6">
        <div>
            <h1 class="text-2xl font-semibold tracking-tight">
                Intégration Zotero
            </h1>
            <p class="text-sm text-muted-foreground">
                Connectez votre bibliothèque Zotero pour importer des références
            </p>
        </div>

        <Card class="rounded-2xl">
            <CardHeader
                class="flex flex-row items-start justify-between space-y-0"
            >
                <div>
                    <CardTitle class="text-base">Zotero</CardTitle>
                    <CardDescription class="mt-1">
                        Gestionnaire de références bibliographiques
                    </CardDescription>
                </div>
                <Badge
                    v-if="zotero.connected"
                    variant="default"
                    class="flex items-center gap-1"
                >
                    <CheckCircle class="h-3 w-3" />
                    Connecté
                </Badge>
                <Badge v-else variant="secondary">Non connecté</Badge>
            </CardHeader>

            <CardContent class="space-y-4">
                <template v-if="zotero.connected">
                    <div class="space-y-2 text-sm">
                        <div class="flex justify-between">
                            <span class="text-muted-foreground">ID Zotero</span>
                            <span class="font-mono">{{
                                zotero.zotero_user_id
                            }}</span>
                        </div>
                        <div class="flex justify-between">
                            <span class="text-muted-foreground"
                                >Connecté le</span
                            >
                            <span>{{ formatDate(zotero.connected_at) }}</span>
                        </div>
                        <div class="flex justify-between">
                            <span class="text-muted-foreground"
                                >Dernière synchro</span
                            >
                            <span>{{ formatDate(zotero.last_synced_at) }}</span>
                        </div>
                    </div>
                    <Button
                        variant="destructive"
                        size="sm"
                        class="gap-2"
                        @click="disconnect"
                    >
                        <Link2Off class="h-4 w-4" />
                        Déconnecter
                    </Button>
                </template>

                <template v-else>
                    <p class="text-sm text-muted-foreground">
                        Autorisez l'accès à votre compte Zotero pour importer
                        automatiquement vos références bibliographiques dans vos
                        projets.
                    </p>
                    <Button
                        size="sm"
                        class="gap-2 bg-brand hover:bg-brand/90"
                        @click="connect"
                    >
                        <Link2 class="h-4 w-4" />
                        Connecter Zotero
                    </Button>
                </template>
            </CardContent>
        </Card>

        <!-- What gets synced -->
        <div class="space-y-3 rounded-xl border border-border bg-muted/30 p-5">
            <h2 class="text-sm font-semibold text-foreground">
                Ce qui est importé depuis Zotero
            </h2>
            <ul
                class="grid grid-cols-2 gap-x-6 gap-y-2 text-sm text-muted-foreground"
            >
                <li class="flex items-center gap-2">
                    <span
                        class="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-brand"
                    />
                    Titre de l'article
                </li>
                <li class="flex items-center gap-2">
                    <span
                        class="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-brand"
                    />
                    Auteurs (nom, prénom)
                </li>
                <li class="flex items-center gap-2">
                    <span
                        class="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-brand"
                    />
                    Année de publication
                </li>
                <li class="flex items-center gap-2">
                    <span
                        class="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-brand"
                    />
                    DOI et identifiants
                </li>
                <li class="flex items-center gap-2">
                    <span
                        class="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-brand"
                    />
                    Résumé (abstract)
                </li>
                <li class="flex items-center gap-2">
                    <span
                        class="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-brand"
                    />
                    Fichiers PDF attachés
                </li>
                <li class="flex items-center gap-2">
                    <span
                        class="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-brand"
                    />
                    Journal et volume
                </li>
                <li class="flex items-center gap-2">
                    <span
                        class="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-brand"
                    />
                    Collections Zotero
                </li>
            </ul>
            <p class="pt-1 text-xs text-muted-foreground">
                La synchronisation s'effectue par projet. Chaque article importé
                est dédupliqué automatiquement.
            </p>
        </div>
    </div>
</template>
